import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { AppState } from 'projects/ngx-messaging/src/app.reducer';
import { DarkModeService } from 'projects/ngx-messaging/src/core/services/dark-mode.service';
import { LoggerService } from 'projects/ngx-messaging/src/core/services/logger.service';
import { DealerSettingName } from 'projects/ngx-messaging/src/modules/chat/interfaces/IDealerSettings';
import { WorkspaceFeatures } from 'projects/ngx-messaging/src/modules/chat/interfaces/IWorkspace';
import { PermissionsService } from 'projects/ngx-messaging/src/modules/chat/services/permissions.service';
import { PhonesService } from 'projects/ngx-messaging/src/modules/chat/services/phones.service';
import { setAuthorizationTokenKey } from 'projects/ngx-messaging/src/modules/chat/store/chat.actions';
import { setLoadingResolve } from 'projects/ngx-messaging/src/modules/shared/store/ui.actions';
import { IUserProfile } from 'projects/ngx-messaging/src/modules/user/interfaces/IUserProfile';
import { AuthenticationService } from 'projects/ngx-messaging/src/modules/user/services/authentication.service';
import { setUser } from 'projects/ngx-messaging/src/modules/user/store/user.actions';
import { Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

@Injectable()
export class AuthResolver {
  user: IUserProfile | null;
  constructor(
    private store: Store<AppState>,
    private authService: AuthenticationService,
    private darkModeService: DarkModeService,
    private permissionsService: PermissionsService,
    private phonesService: PhonesService,
    private logger: LoggerService
  ) {
    this.setUserSubscription();
  }

  setUserSubscription(): void {
    this.store.select('user').subscribe(({ user }) => {
      this.user = user;
    });
  }

  resolve(route: ActivatedRouteSnapshot): Observable<unknown> {
    this.store.dispatch(setLoadingResolve({ value: true }));
    const signature = route.queryParams['signature'] as string;
    if (signature) {
      const accountUID = route.queryParams['accountUID'] as string;
      let phoneNumber = route.queryParams['phoneNumber'] as string;
      const subAccountUID = route.queryParams['subAccountUID'] as string;
      const timestamp = route.queryParams['timestamp'] as string;
      const apiKey = route.queryParams['apiKey'] as string;
      phoneNumber = this.phonesService.setPhoneNumber(phoneNumber);
      return this.authService
        .getTokenBySignature({ signature, phoneNumber, accountUID, subAccountUID, timestamp, apiKey })
        .pipe(
          switchMap((data) => {
            localStorage.setItem('messaging-api-token', data.token);
            localStorage.removeItem('messaging-token');
            this.logger.debug('Token obtained via signature');
            return this.getResolve(route, true);
          }),
          catchError((error) => {
            const token = localStorage.getItem('messaging-api-token');
            if (token) {
              return this.getResolve(route, true);
            }
            this.logger.error(error, 'AuthResolver.getTokenBySignature');
            this.authService.logout(null, true, true);
            return of();
          })
        );
    }
    return this.getResolve(route);
  }

  getResolve(route: ActivatedRouteSnapshot, isSignature = false): Observable<unknown> {
    if (this.user) {
      this.store.dispatch(setLoadingResolve({ value: false }));
      return of(this.user);
    }
    const dealerId = route.queryParams['dealerId'] as string;
    const tokenAuthorization = route.queryParams['authorization'] as string;
    if (tokenAuthorization) {
      this.store.dispatch(setAuthorizationTokenKey({ key: tokenAuthorization }));
    }
    const dealerIdLocal = localStorage.getItem('dealerId') as string;
    if (dealerId && dealerIdLocal && dealerId !== dealerIdLocal) {
      return this.authService.changeTokenByDealerID(dealerId as string).pipe(
        switchMap((res) => {
          this.authService.setTokenAndDealerId(res.token, dealerId);
          return this.getUserInfo();
        }),
        catchError(() => {
          this.authService.logout(null, true, isSignature);
          return of();
        })
      );
    }
    return this.getUserInfo().pipe(
      catchError((err) => {
        this.logger.error(err, 'AuthResolver.getUserInfo');
        this.authService.logout(null, true, isSignature);
        return of();
      })
    );
  }

  getUserInfo() {
    return this.authService.getUserProfile().pipe(
      first(),
      map((user) => {
        this.store.dispatch(setUser({ profileInfo: user }));
        this.darkModeService.setTheme();
        if (
          this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.needCheckEnableTextConversations]) &&
          user?.dealerSettings?.length &&
          !this.permissionsService.checkDealerSetting(DealerSettingName.enableTextConversation)
        ) {
          throw { status: 401, message: 'Cannot use workspace' };
        }
        return user;
      })
    );
  }
}
